import { ArrowLeft, School, MapPin, Eye, EyeOff, Route, Type } from 'lucide-react';
import type { SchoolStatus } from '../../types';

export type StatusFilter = Set<SchoolStatus>;

interface StateStatsBarProps {
  stateName: string;
  stats: { totalSchools: number; blastCount: number; competitorCount: number; openCount: number };
  territoryCount: number;
  activeFilters: StatusFilter;
  onToggleFilter: (status: SchoolStatus) => void;
  onShowAll: () => void;
  showTerritories: boolean;
  onToggleTerritories: () => void;
  showHighways: boolean;
  onToggleHighways: () => void;
  showCities: boolean;
  onToggleCities: () => void;
  onBack: () => void;
}

export default function StateStatsBar({
  stateName, stats, territoryCount, activeFilters, onToggleFilter, onShowAll,
  showTerritories, onToggleTerritories, showHighways, onToggleHighways,
  showCities, onToggleCities, onBack,
}: StateStatsBarProps) {
  const allActive = activeFilters.size === 3;
  const noneActive = activeFilters.size === 0;

  const pills: {
    status: SchoolStatus;
    count: number;
    label: string;
    dotColor: string;
    activeBg: string;
    activeText: string;
    inactiveBg: string;
    inactiveText: string;
  }[] = [
    {
      status: 'blast', count: stats.blastCount, label: 'Blast',
      dotColor: 'bg-green-500',
      activeBg: 'bg-green-50 dark:bg-green-900/30',
      activeText: 'text-green-700 dark:text-green-300',
      inactiveBg: 'bg-gray-100 dark:bg-gray-700/50',
      inactiveText: 'text-gray-400 dark:text-gray-500',
    },
    {
      status: 'competitor', count: stats.competitorCount, label: 'Competitor',
      dotColor: 'bg-red-500',
      activeBg: 'bg-red-50 dark:bg-red-900/30',
      activeText: 'text-red-700 dark:text-red-300',
      inactiveBg: 'bg-gray-100 dark:bg-gray-700/50',
      inactiveText: 'text-gray-400 dark:text-gray-500',
    },
    {
      status: 'open', count: stats.openCount, label: 'Open',
      dotColor: 'bg-gray-400',
      activeBg: 'bg-gray-50 dark:bg-gray-700',
      activeText: 'text-gray-600 dark:text-gray-400',
      inactiveBg: 'bg-gray-100 dark:bg-gray-700/50',
      inactiveText: 'text-gray-400 dark:text-gray-500',
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        {/* Back + title */}
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to US Map
          </button>
          <span className="text-gray-300 dark:text-gray-600">|</span>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{stateName}</h2>
        </div>

        {/* Stats pills — clickable filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Total schools (non-interactive) */}
          <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-700 rounded-full px-3 py-1">
            <School className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {stats.totalSchools.toLocaleString()} schools
            </span>
          </div>

          {/* Divider */}
          <span className="text-gray-300 dark:text-gray-600">|</span>

          {/* Toggleable status pills */}
          {pills.map(pill => {
            const isActive = activeFilters.has(pill.status);
            return (
              <button
                key={pill.status}
                onClick={() => onToggleFilter(pill.status)}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1 transition-all cursor-pointer ${
                  isActive
                    ? `${pill.activeBg} ${pill.activeText}`
                    : `${pill.inactiveBg} ${pill.inactiveText} opacity-60`
                }`}
                title={isActive ? `Hide ${pill.label} schools` : `Show ${pill.label} schools`}
              >
                <span className={`h-2.5 w-2.5 rounded-full ${isActive ? pill.dotColor : 'bg-gray-300 dark:bg-gray-600'}`} />
                <span className="text-sm font-medium">
                  {pill.count.toLocaleString()} {pill.label}
                </span>
              </button>
            );
          })}

          {/* Show all / Clear all toggle */}
          <button
            onClick={onShowAll}
            className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors ml-1"
            title={allActive ? 'Clear all filters' : 'Show all schools'}
          >
            {allActive ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {allActive ? 'Clear' : 'All'}
          </button>

          {/* Divider */}
          <span className="text-gray-300 dark:text-gray-600">|</span>

          {/* Territories toggle */}
          <button
            onClick={onToggleTerritories}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 transition-all cursor-pointer ${
              showTerritories
                ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                : 'bg-gray-100 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 opacity-60'
            }`}
            title={showTerritories ? 'Hide territory zones' : 'Show territory zones'}
          >
            <MapPin className={`h-4 w-4 ${showTerritories ? 'text-indigo-500' : 'text-gray-400 dark:text-gray-500'}`} />
            <span className="text-sm font-medium">
              {territoryCount} {territoryCount === 1 ? 'territory' : 'territories'}
            </span>
          </button>

          {/* Highways toggle */}
          <button
            onClick={onToggleHighways}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 transition-all cursor-pointer ${
              showHighways
                ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                : 'bg-gray-100 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 opacity-60'
            }`}
            title={showHighways ? 'Hide highways' : 'Show highways'}
          >
            <Route className={`h-4 w-4 ${showHighways ? 'text-yellow-500' : 'text-gray-400 dark:text-gray-500'}`} />
            <span className="text-sm font-medium">Highways</span>
          </button>

          {/* Cities toggle */}
          <button
            onClick={onToggleCities}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 transition-all cursor-pointer ${
              showCities
                ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300'
                : 'bg-gray-100 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 opacity-60'
            }`}
            title={showCities ? 'Hide city labels' : 'Show city labels'}
          >
            <Type className={`h-4 w-4 ${showCities ? 'text-sky-500' : 'text-gray-400 dark:text-gray-500'}`} />
            <span className="text-sm font-medium">Cities</span>
          </button>
        </div>
      </div>
    </div>
  );
}
